import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getSiteSettings } from '../lib/api';
import './AboutPage.css';
import { useSEO } from '../lib/seo';

/* ══════════════════════════════════════════
   SVG ICON COMPONENTS
══════════════════════════════════════════ */
const HerbIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M21 3C21 3 14.5 3.5 9.5 8.5C5 13 3.5 20.5 3.5 20.5C3.5 20.5 7.5 18.5 10.5 16.5C10.8 17.8 11.8 19.2 13 20L14 18.8C12.8 17.5 12.5 16 12.5 16C16 13 21 3 21 3Z" fill={color} />
    <path d="M3.5 20.5L9.5 14.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const FallingLeafIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M17 4C17 4 17 14.5 6.5 19L5.5 17.8C9.5 14.5 11 9.5 11 9.5C11 9.5 8 13.5 5 16.5L4 15.5C8.5 9 17 4 17 4Z" fill={color} />
    <path d="M5 16.5L3.5 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SeedlingIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="12" y1="22" x2="12" y2="9" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M12 16C12 16 8.5 13.5 8.5 10.5C8.5 7.5 12 5.5 12 5.5C12 5.5 15.5 7.5 15.5 10.5C15.5 13.5 12 16 12 16Z" fill={color} />
  </svg>
);

const TreeIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2L20 14H14.5V21H9.5V14H4L12 2Z" fill={color} />
  </svg>
);

const CheckIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8.5L6.5 12L13 5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChatIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 6C3 4.9 3.9 4 5 4H19C20.1 4 21 4.9 21 6V15C21 16.1 20.1 17 19 17H13.5L8.5 21V17H5C3.9 17 3 16.1 3 15V6Z" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="8" cy="10.5" r="1" fill={color} />
    <circle cx="12" cy="10.5" r="1" fill={color} />
    <circle cx="16" cy="10.5" r="1" fill={color} />
  </svg>
);

const SunIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="4" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.5" />
    <line x1="12" y1="2.5" x2="12" y2="5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="12" y1="19" x2="12" y2="21.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="2.5" y1="12" x2="5" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="19" y1="12" x2="21.5" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="5.93" y1="5.93" x2="7.76" y2="7.76" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="16.24" y1="16.24" x2="18.07" y2="18.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="5.93" y1="18.07" x2="7.76" y2="16.24" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="16.24" y1="7.76" x2="18.07" y2="5.93" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const BoxIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 8.5L12 4L21 8.5V18.5L12 23L3 18.5V8.5Z" fill={color} fillOpacity="0.14" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M3 8.5L12 13L21 8.5" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    <line x1="12" y1="13" x2="12" y2="23" stroke={color} strokeWidth="1.5" />
  </svg>
);

const HouseIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 11L12 3L21 11V21H15V15H9V21H3V11Z" fill={color} fillOpacity="0.14" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

/* ══════════════════════════════════════════
   MARQUEE
══════════════════════════════════════════ */
const MARQUEE_ICONS = [
  <HerbIcon size={15} />,
  <FallingLeafIcon size={15} />,
  <SeedlingIcon size={15} />,
  <TreeIcon size={15} />,
];

const MARQUEE_TEXTS = [
  'Farm to doorstep', 'ICON:0',
  '100+ cities', 'ICON:1',
  '14-day guarantee', 'ICON:2',
  '4 farms', 'ICON:3',
  'One tree per order', 'ICON:0',
  'Farm to doorstep', 'ICON:1',
  '100+ cities', 'ICON:2',
  '14-day guarantee', 'ICON:3',
  '4 farms', 'ICON:0',
  'One tree per order', 'ICON:1',
];

/* ══════════════════════════════════════════
   STATIC FALLBACK DATA
══════════════════════════════════════════ */
const DEFAULT_STATS = [
  { value: '2019', label: 'Founded in Pune' },
  { value: '100+', label: 'Cities served' },
  { value: '2,00,000+', label: 'Plants delivered' },
  { value: '4.8★', label: 'Average rating' },
];

const TIMELINE = [
  { year: '2019', title: 'Three acres, one greenhouse', text: 'Naturiq starts as a single nursery outside Pune, growing succulents and easy-care plants for local nurseries.', tag: 'The beginning', color: '#2d6a4f' },
  { year: '2021', title: 'We go online', text: 'The farm opens a storefront on the internet. First delivery: a Pothos, to a flat two streets away.', tag: null, color: '#40916c' },
  { year: '2023', title: 'Delivering to 100 cities', text: 'Survival packaging and a 14-day guarantee make shipping a living plant across India something we can promise.', tag: 'Milestone', color: '#52b788' },
  { year: '2025', title: 'The plantation pledge', text: 'A share of every order starts funding native tree plantation drives — built into the price, not an optional add-on.', tag: null, color: '#74c69d' },
  { year: '2026', title: 'Where we are now', text: 'Four farms, a plant-care helpline that actually picks up, and a 100,000-strong community of plant parents.', tag: 'Today', color: '#95d5b2' },
];

const DEFAULT_VALUES = [
  { title: 'Grown, not just sold', text: 'Every plant spends 4–8 weeks at one of our farms before it ever reaches a cart.', icon: <HerbIcon size={22} />, bg: '#d8f3dc' },
  { title: 'Honest about care', text: "If a plant is hard to keep alive, our listing says so. We'd rather lose a sale than gain a wilted plant.", icon: <CheckIcon size={17} />, bg: '#b7e4c7' },
  { title: 'Support after the sale', text: 'Our plant-care helpline answers real questions — not a script. Most replies in under an hour.', icon: <ChatIcon size={22} />, bg: '#95d5b2' },
  { title: 'A tree for every order', text: 'A portion of every sale funds native-species plantation drives — quietly, with no checkbox required.', icon: <TreeIcon size={22} />, bg: '#74c69d' },
];

const PROCESS = [
  { step: '01', icon: <HerbIcon size={26} />, title: 'Seed & propagate', text: 'Every plant begins in our controlled propagation beds. We monitor humidity, soil pH, and root health daily before anything moves forward.' },
  { step: '02', icon: <SunIcon size={26} />, title: 'Weeks of sunlight', text: "Plants spend 4–8 weeks maturing in our greenhouses. No shortcuts — if a batch isn't ready, it doesn't ship." },
  { step: '03', icon: <BoxIcon size={26} />, title: 'Survival-packed', text: 'Our custom packaging keeps plants alive for 5–7 days in transit. Roots cushioned, leaves braced, hydration controlled.' },
  { step: '04', icon: <HouseIcon size={26} />, title: 'At your door', text: "Delivered with a care card specific to your plant, and our helpline number on the box — because questions don't wait." },
];

const DEFAULT_BIG_NUMBERS = [
  { num: '4', unit: 'farms', desc: 'Across Maharashtra, Karnataka & Rajasthan' },
  { num: '8', unit: 'weeks', desc: 'Minimum time every plant spends with us before shipping' },
  { num: '14', unit: 'days', desc: 'Happiness guarantee — no ifs, no buts' },
  { num: '<1hr', unit: 'response', desc: 'Average helpline reply time, including weekends' },
];

const PROCESS_ICONS = {
  herb:  <HerbIcon size={26} />,
  sun:   <SunIcon size={26} />,
  box:   <BoxIcon size={26} />,
  house: <HouseIcon size={26} />,
};

const GALLERY = [
  { src: 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=900&h=1100&fit=crop&auto=format&q=80', label: 'Our Pune farm — where it all started' },
  { src: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=400&fit=crop&auto=format&q=80', label: 'Greenhouse propagation beds' },
  { src: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&h=400&fit=crop&auto=format&q=80', label: 'Packing & dispatch team' },
  { src: 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=600&h=400&fit=crop&auto=format&q=80', label: 'Native tree plantation drive, Karnataka' },
  { src: 'https://images.unsplash.com/photo-1546842931-886c185b4c8c?w=600&h=400&fit=crop&auto=format&q=80', label: 'Seedling nursery, Maharashtra' },
];

/* ══════════════════════════════════════════
   ANIMATION HOOK
══════════════════════════════════════════ */
function AnimatedSection({ children, className = '' }) {
  return <div className={className}>{children}</div>;
}

/* ══════════════════════════════════════════
   SECTION RENDERERS
   Each renderer receives its section data from the API (or falls back to
   static defaults when the admin hasn't customised that section yet).
══════════════════════════════════════════ */

function HeroSection({ section }) {
  const title = section?.title || null;
  const subtitle = section?.subtitle || null;
  const description = section?.description || null;
  const ctaText = section?.ctaText || 'Shop plants';
  const ctaLink = section?.ctaLink || '/collections/plants';
  const heroImg = section?.images?.[0]?.url || 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=1600&h=900&fit=crop&auto=format&q=80';

  return (
    <section className="ab-hero" style={section?.bgColor ? { background: section.bgColor } : {}}>
      <div className="ab-hero__bg">
        <img src={heroImg} alt="About hero" className="ab-hero__img" />
        <div className="ab-hero__veil" />
      </div>

      <div className="ab-hero__leaf ab-hero__leaf--1" aria-hidden><HerbIcon size={48} color="#2d6a4f" /></div>
      <div className="ab-hero__leaf ab-hero__leaf--2" aria-hidden><FallingLeafIcon size={32} color="#2d6a4f" /></div>
      <div className="ab-hero__leaf ab-hero__leaf--3" aria-hidden><SeedlingIcon size={24} color="#2d6a4f" /></div>

      <div className="ab-hero__content ab-hero__content--in">
        <div className="ab-hero__kicker">
          <span className="ab-hero__kicker-dot" />
          Since 2019 · Pune, India
        </div>
        {title ? (
          <h1 className="ab-hero__h1"><span className="ab-hero__h1-line">{title}</span></h1>
        ) : (
          <h1 className="ab-hero__h1">
            <span className="ab-hero__h1-line">We farm it.</span>
            <span className="ab-hero__h1-line ab-hero__h1-line--accent">We pack it.</span>
            <span className="ab-hero__h1-line">We care after it.</span>
          </h1>
        )}
        {(subtitle || description) && (
          <p className="ab-hero__sub">{subtitle || description}</p>
        )}
        {!subtitle && !description && (
          <p className="ab-hero__sub">
            Most plant companies sell plants. We raise them for weeks before they leave our doors — then stay in touch long after.
          </p>
        )}
        <div className="ab-hero__ctas">
          <Link to={ctaLink} className="ab-btn ab-btn--leaf">{ctaText}</Link>
          <a href="#our-story" className="ab-btn ab-btn--ghost">Our story ↓</a>
        </div>
      </div>

      <div className="ab-hero__stats">
        {(section?.heroStats?.length > 0 ? section.heroStats : DEFAULT_STATS).map((s, i) => (
          <div key={s.label + i} className="ab-hero__stat" style={{ animationDelay: `${0.6 + i * 0.1}s` }}>
            <strong>{s.value}</strong>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function MissionSection({ section }) {
  if (!section) return null;
  const img = section.images?.[0]?.url || 'https://images.unsplash.com/photo-1545241047-6083a3684587?w=700&h=920&fit=crop&auto=format&q=80';

  return (
    <section
      className="ab-story"
      id="our-story"
      style={{
        ...(section.bgColor ? { background: section.bgColor } : {}),
        ...(section.paddingTop != null ? { paddingTop: section.paddingTop } : {}),
        ...(section.paddingBottom != null ? { paddingBottom: section.paddingBottom } : {}),
        ...(section.paddingSide != null ? { paddingLeft: section.paddingSide, paddingRight: section.paddingSide } : {}),
      }}
    >
      <AnimatedSection className="ab-story__img-col">
        <div className="ab-story__img-frame">
          <img src={img} alt={section.title || 'Our mission'} />
          <div className="ab-story__img-badge">
            <div className="ab-story__img-badge-year">2019</div>
            <div className="ab-story__img-badge-label">where it started</div>
          </div>
          <div className="ab-story__deco-ring" aria-hidden />
        </div>
      </AnimatedSection>

      <AnimatedSection className="ab-story__text-col" delay={120}>
        {section.subtitle && <p className="ab-eyebrow">{section.subtitle}</p>}
        {section.title && <h2 className="ab-h2">{section.title}</h2>}
        {!section.title && <h2 className="ab-h2">It started with a greenhouse nobody else wanted</h2>}
        {section.description ? (
          <p className="ab-body">{section.description}</p>
        ) : (
          <>
            <p className="ab-body">In 2019, two friends took over a half-abandoned greenhouse outside Pune. Within a year, the same soil that had failed once was producing some of the healthiest Monsteras and Snake Plants anyone had seen.</p>
            <p className="ab-body">We didn't set out to build an e-commerce brand. We set out to keep plants alive long enough to sell them — which turned out to be the harder, rarer skill.</p>
          </>
        )}
        <ul className="ab-checklist">
          {['Every plant raised at our own farms', '14-day happiness guarantee', 'Plant-care helpline that picks up', 'One tree funded per order'].map(c => (
            <li key={c}>
              <span className="ab-checklist__icon"><CheckIcon size={13} color="var(--forest)" /></span>
              {c}
            </li>
          ))}
        </ul>
        {section.ctaText && section.ctaLink && (
          <Link to={section.ctaLink} className="ab-btn ab-btn--leaf">{section.ctaText}</Link>
        )}
        {!section.ctaText && <Link to="/collections/plants" className="ab-btn ab-btn--leaf">Browse the collection</Link>}
      </AnimatedSection>
    </section>
  );
}

const TIMELINE_COLORS = ['#2d6a4f', '#40916c', '#52b788', '#74c69d', '#95d5b2'];

function StorySection({ section }) {
  const [activeTimelineItem, setActiveTimelineItem] = useState(0);
  const timeline = section?.timelineItems?.length > 0
    ? section.timelineItems.map((t, i) => ({ ...t, color: t.color || TIMELINE_COLORS[i % TIMELINE_COLORS.length] }))
    : TIMELINE;

  return (
    <section
      className="ab-timeline"
      id="story"
      style={{
        ...(section?.bgColor ? { background: section.bgColor } : {}),
        ...(section?.paddingTop != null ? { paddingTop: section.paddingTop } : {}),
        ...(section?.paddingBottom != null ? { paddingBottom: section.paddingBottom } : {}),
        ...(section?.paddingSide != null ? { paddingLeft: section.paddingSide, paddingRight: section.paddingSide } : {}),
      }}
    >
      <AnimatedSection>
        <p className="ab-eyebrow ab-eyebrow--center">{section?.subtitle || 'How we got here'}</p>
        <h2 className="ab-h2 ab-h2--center">{section?.title || 'Five years. Four farms. One mission.'}</h2>
        {section?.description && <p className="ab-subhead ab-subhead--center">{section.description}</p>}
      </AnimatedSection>

      <div className="ab-tl">
        <div className="ab-tl__nav">
          {timeline.map((t, i) => (
            <button
              key={t.year + i}
              className={`ab-tl__nav-btn ${activeTimelineItem === i ? 'ab-tl__nav-btn--active' : ''}`}
              onClick={() => setActiveTimelineItem(i)}
              style={{ '--tl-color': t.color }}
            >
              <span className="ab-tl__nav-year">{t.year}</span>
              <span className="ab-tl__nav-label">{t.title}</span>
            </button>
          ))}
          <div
            className="ab-tl__nav-indicator"
            style={{ transform: `translateY(${activeTimelineItem * 100}%)`, background: timeline[activeTimelineItem]?.color }}
          />
        </div>
        <div className="ab-tl__panel">
          {timeline.map((t, i) => (
            <div
              key={t.year + i}
              className={`ab-tl__entry ${activeTimelineItem === i ? 'ab-tl__entry--active' : ''}`}
              aria-hidden={activeTimelineItem !== i}
            >
              <div className="ab-tl__entry-year" style={{ color: t.color }}>{t.year}</div>
              {t.tag && <span className="ab-tl__entry-tag" style={{ background: t.color }}>{t.tag}</span>}
              <h3 className="ab-tl__entry-title">{t.title}</h3>
              <p className="ab-tl__entry-text">{t.text}</p>
              <div className="ab-tl__entry-bar" style={{ background: t.color }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamSection({ section }) {
  if (!section) return null;
  const members = section.teamMembers || [];

  return (
    <section
      className="ab-team"
      style={{
        ...(section.bgColor ? { background: section.bgColor } : {}),
        ...(section.paddingTop != null ? { paddingTop: section.paddingTop } : {}),
        ...(section.paddingBottom != null ? { paddingBottom: section.paddingBottom } : {}),
        ...(section.paddingSide != null ? { paddingLeft: section.paddingSide, paddingRight: section.paddingSide } : {}),
      }}
    >
      <AnimatedSection>
        {section.subtitle && <p className="ab-eyebrow ab-eyebrow--center">{section.subtitle}</p>}
        <h2 className="ab-h2 ab-h2--center">{section.title || 'Meet the Team'}</h2>
      </AnimatedSection>
      {members.length > 0 && (
        <div className="ab-team__grid">
          {members.map((m, i) => (
            <AnimatedSection key={i} className="ab-team__card" delay={i * 80}>
              {m.image && (
                <div className="ab-team__card-img">
                  <img src={m.image} alt={m.name} />
                </div>
              )}
              {!m.image && (
                <div className="ab-team__card-img ab-team__card-img--placeholder">
                  <HerbIcon size={36} color="#2d6a4f" />
                </div>
              )}
              <h3 className="ab-team__card-name">{m.name}</h3>
              {m.role && <p className="ab-team__card-role">{m.role}</p>}
              {m.bio && <p className="ab-team__card-bio">{m.bio}</p>}
            </AnimatedSection>
          ))}
        </div>
      )}
    </section>
  );
}

function ValuesSection({ section }) {
  const cards = section?.valueCards || [];
  const useAdminCards = cards.length > 0;
  const displayCards = useAdminCards ? cards : DEFAULT_VALUES;

  return (
    <section
      className="ab-values"
      style={{
        ...(section?.bgColor ? { background: section.bgColor } : {}),
        ...(section?.paddingTop != null ? { paddingTop: section.paddingTop } : {}),
        ...(section?.paddingBottom != null ? { paddingBottom: section.paddingBottom } : {}),
        ...(section?.paddingSide != null ? { paddingLeft: section.paddingSide, paddingRight: section.paddingSide } : {}),
      }}
    >
      <div className="ab-values__head">
        <AnimatedSection>
          <p className="ab-eyebrow ab-eyebrow--center">{section?.subtitle || 'What we hold to'}</p>
          <h2 className="ab-h2 ab-h2--center">{section?.title || 'Four principles we don\'t compromise on'}</h2>
        </AnimatedSection>
      </div>
      <div className="ab-values__grid">
        {displayCards.map((v, i) => (
          <AnimatedSection key={v.heading || v.title || i} className="ab-value-card" delay={i * 80}>
            {useAdminCards ? (
              <>
                <div className="ab-value-card__icon" style={{ background: '#d8f3dc' }}>
                  <span style={{ fontSize: 22 }}>{v.icon || '🌿'}</span>
                </div>
                <div className="ab-value-card__num">0{i + 1}</div>
                <h3 className="ab-value-card__title">{v.heading}</h3>
                <p className="ab-value-card__text">{v.description}</p>
              </>
            ) : (
              <>
                <div className="ab-value-card__icon" style={{ background: v.bg }}>{v.icon}</div>
                <div className="ab-value-card__num">0{i + 1}</div>
                <h3 className="ab-value-card__title">{v.title}</h3>
                <p className="ab-value-card__text">{v.text}</p>
              </>
            )}
          </AnimatedSection>
        ))}
      </div>
    </section>
  );
}

function StatsSection({ section }) {
  const statItems = section?.statItems || [];
  const useAdminStats = statItems.length > 0;
  const displayStats = useAdminStats ? statItems : DEFAULT_BIG_NUMBERS;

  return (
    <section
      className="ab-bignums"
      style={{
        ...(section?.bgColor ? { '--bignums-bg': section.bgColor } : {}),
        ...(section?.paddingTop != null ? { paddingTop: section.paddingTop } : {}),
        ...(section?.paddingBottom != null ? { paddingBottom: section.paddingBottom } : {}),
        ...(section?.paddingSide != null ? { paddingLeft: section.paddingSide, paddingRight: section.paddingSide } : {}),
      }}
    >
      <div className="ab-bignums__bg-text" aria-hidden>NATURIQ</div>
      <div className="ab-bignums__inner">
        <AnimatedSection className="ab-bignums__head">
          <p className="ab-eyebrow" style={{ color: '#95d5b2' }}>{section?.subtitle || 'By the numbers'}</p>
          <h2 className="ab-h2" style={{ color: '#fff' }}>{section?.title || 'Standards we hold ourselves to'}</h2>
        </AnimatedSection>
        <div className="ab-bignums__grid">
          {useAdminStats ? (
            displayStats.map((s, i) => (
              <AnimatedSection key={i} className="ab-bignum" delay={i * 90}>
                <div className="ab-bignum__top">
                  <span className="ab-bignum__num">{s.number}</span>
                </div>
                <p className="ab-bignum__desc">{s.label}</p>
              </AnimatedSection>
            ))
          ) : (
            displayStats.map((b, i) => (
              <AnimatedSection key={b.unit} className="ab-bignum" delay={i * 90}>
                <div className="ab-bignum__top">
                  <span className="ab-bignum__num">{b.num}</span>
                  <span className="ab-bignum__unit">{b.unit}</span>
                </div>
                <p className="ab-bignum__desc">{b.desc}</p>
              </AnimatedSection>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ section }) {
  if (!section) return null;
  const bgImg = section.images?.[0]?.url;
  const title = section.title || 'Ready to Bring Nature Home?';
  const description = section.description || 'Browse our curated collection of indoor plants and find the perfect green companion for your space.';
  const ctaText = section.ctaText || 'Shop Now';
  const ctaLink = section.ctaLink || '/collections/plants';

  return (
    <section
      className="ab-cta"
      style={{
        ...(section.bgColor ? { background: section.bgColor } : {}),
        ...(bgImg ? { backgroundImage: `url(${bgImg})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
        ...(section.paddingTop != null ? { paddingTop: section.paddingTop } : {}),
        ...(section.paddingBottom != null ? { paddingBottom: section.paddingBottom } : {}),
        ...(section.paddingSide != null ? { paddingLeft: section.paddingSide, paddingRight: section.paddingSide } : {}),
      }}
    >
      {bgImg && <div className="ab-cta__veil" />}
      <AnimatedSection className="ab-cta__inner">
        <h2 className="ab-cta__title">{title}</h2>
        <p className="ab-cta__desc">{description}</p>
        <Link to={ctaLink} className="ab-btn ab-btn--leaf ab-btn--large">{ctaText}</Link>
      </AnimatedSection>
    </section>
  );
}

/* ══════════════════════════════════════════
   PAGE COMPONENT
══════════════════════════════════════════ */
export default function AboutPage() {
  useSEO({
    title: 'About Us — Our Story & Mission',
    description: "Learn about Naturiq — India's premium farm-to-home indoor plant brand. Meet our team, our farm and our mission to bring nature into every home.",
    url: '/about',
  });

  const [sections, setSections] = useState(null); // null = loading

  useEffect(() => {
    getSiteSettings()
      .then(data => {
        const raw = Array.isArray(data.aboutSections) ? data.aboutSections : [];
        // Sort by admin-defined order
        const sorted = [...raw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setSections(sorted);
      })
      .catch(() => {
        setSections([]); // fall back to defaults on error
      });
  }, []);

  // ── Admin live-preview bridge ────────────────────────────────────────────
  // When this page is embedded in an iframe by the admin's About Page Editor
  // (?preview=1), it listens for postMessage'd aboutSections so unsaved edits
  // show up instantly without round-tripping through the database, and
  // reports section clicks back to the parent so clicking any section in the
  // live preview opens it for editing — same bridge as the Home Page editor.
  const [highlightedSection, setHighlightedSection] = useState(null);

  useEffect(() => {
    const isEmbedded = window.self !== window.top;
    if (!isEmbedded) return;

    const handleMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.source !== 'naturiq-admin-preview') return;
      if (msg.type === 'aboutSections' && Array.isArray(msg.payload) && msg.payload.length > 0) {
        setSections([...msg.payload].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
      if (msg.type === 'highlightSection') {
        setHighlightedSection(msg.payload || null);
      }
    };
    window.addEventListener('message', handleMessage);

    // Let the parent know the preview frame is ready to receive updates,
    // in case it's already loaded and waiting to push the current draft.
    window.parent.postMessage({ source: 'naturiq-preview-frame', type: 'ready' }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const isPreviewMode = window.self !== window.top;

  // Wrap a rendered section with a click-to-select overlay when embedded in
  // the admin live preview. A no-op for normal shoppers.
  const previewSection = (key, element) => {
    if (!element) return null;
    if (!isPreviewMode) return element;
    const isHighlighted = highlightedSection === key;
    return (
      <div
        key={`preview-${key}`}
        style={{ position: 'relative', outline: isHighlighted ? '3px solid #2563eb' : '2px solid transparent', outlineOffset: '-2px', transition: 'outline 0.15s', cursor: 'pointer', boxSizing: 'border-box' }}
        onClick={() => window.parent.postMessage({ source: 'naturiq-preview-frame', type: 'sectionClick', payload: key }, '*')}
        title="Edit this section"
      >
        {element}
        {isHighlighted && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: '#2563eb', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 8px', letterSpacing: '0.05em', pointerEvents: 'none', zIndex: 9999, fontFamily: 'system-ui, sans-serif' }}>
            ✏️ Editing: {key}
          </div>
        )}
        {!isHighlighted && (
          <div className="preview-hover-hint" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9998 }}>
            <span style={{ position: 'absolute', top: 6, right: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '4px', opacity: 0, transition: 'opacity 0.15s', fontFamily: 'system-ui, sans-serif' }} className="preview-edit-label">
              Click to edit
            </span>
          </div>
        )}
      </div>
    );
  };

  // Build a lookup by key for easy access
  const sec = {};
  if (sections) {
    sections.forEach(s => { sec[s.key] = s; });
  }

  // Helper: is a section visible? (enabled OR not yet in DB = show default)
  const isVisible = key => !sections || !sec[key] || sec[key].enabled !== false;

  // Marquee is always rendered between hero and the rest, so we render it
  // separately. For the remaining sections we honour the admin-defined order.
  const SECTION_RENDERERS = {
    hero:    () => isVisible('hero') && <HeroSection key="hero" section={sec['hero']} />,
    mission: () => isVisible('mission') && <MissionSection key="mission" section={sec['mission']} />,
    story:   () => isVisible('story') && <StorySection key="story" section={sec['story']} />,
    team:    () => isVisible('team') && sec['team'] && sec['team'].teamMembers?.length > 0
                    ? <TeamSection key="team" section={sec['team']} />
                    : null,
    values:  () => isVisible('values') && <ValuesSection key="values" section={sec['values']} />,
    stats:   () => isVisible('stats') && <StatsSection key="stats" section={sec['stats']} />,
    cta:     () => isVisible('cta') && sec['cta'] && <CtaSection key="cta" section={sec['cta']} />,
  };

  // Build ordered list of section keys from admin settings (or default order)
  const DEFAULT_ORDER = ['hero', 'mission', 'story', 'team', 'values', 'stats', 'cta'];
  const orderedKeys = sections && sections.length > 0
    ? sections.map(s => s.key).filter(k => DEFAULT_ORDER.includes(k))
    : DEFAULT_ORDER;

  // Ensure any key not in admin data still appears (at end)
  DEFAULT_ORDER.forEach(k => { if (!orderedKeys.includes(k)) orderedKeys.push(k); });

  // Find hero index to inject the marquee right after it
  const heroIdx = orderedKeys.indexOf('hero');

  if (sections === null) {
    return (
      <main className="about-page">
        <div className="about-skel">
          <div className="about-skel__hero skel" />
          <div className="about-skel__body">
            <div className="skel" style={{ width: '20%', height: 13, borderRadius: 5, margin: '0 auto' }} />
            <div className="skel" style={{ width: '55%', height: 38, borderRadius: 8, margin: '12px auto 0' }} />
            <div className="skel" style={{ width: '70%', height: 18, borderRadius: 6, margin: '14px auto 0' }} />
            <div className="skel" style={{ width: '40%', height: 18, borderRadius: 6, margin: '6px auto 0' }} />
            <div className="about-skel__cards">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="about-skel__card">
                  <div className="skel" style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto' }} />
                  <div className="skel" style={{ width: '60%', height: 16, borderRadius: 6, margin: '14px auto 0' }} />
                  <div className="skel" style={{ width: '80%', height: 12, borderRadius: 5, margin: '8px auto 0' }} />
                  <div className="skel" style={{ width: '65%', height: 12, borderRadius: 5, margin: '6px auto 0' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="about-page">
      {orderedKeys.map((key, i) => {
        const renderer = SECTION_RENDERERS[key];
        const rendered = renderer ? renderer() : null;
        return (
          <React.Fragment key={key}>
            {previewSection(key, rendered)}
            {/* Inject marquee immediately after hero */}
            {i === heroIdx && (
              <div className="ab-marquee" aria-hidden>
                <div className="ab-marquee__track">
                  {MARQUEE_TEXTS.map((t, j) => {
                    if (t.startsWith('ICON:')) {
                      const idx = parseInt(t.split(':')[1], 10);
                      return <span key={j} className="ab-marquee__item ab-marquee__item--icon">{MARQUEE_ICONS[idx]}</span>;
                    }
                    return <span key={j} className="ab-marquee__item">{t}</span>;
                  })}
                </div>
              </div>
            )}
            {/* Inject process section after story */}
            {key === 'story' && isVisible('story') && previewSection('story', (() => {
              const adminSteps = sec['story']?.processSteps;
              const steps = adminSteps?.length > 0
                ? adminSteps.map((p, pi) => ({
                    step: String(pi + 1).padStart(2, '0'),
                    icon: PROCESS_ICONS[p.icon] || PROCESS_ICONS.herb,
                    title: p.title,
                    text: p.text,
                  }))
                : PROCESS;
              return (
                <section className="ab-process">
                  <AnimatedSection>
                    <p className="ab-eyebrow ab-eyebrow--center">Seed to doorstep</p>
                    <h2 className="ab-h2 ab-h2--center">How a plant becomes yours</h2>
                    <p className="ab-subhead ab-subhead--center">Every plant has a journey before it reaches your cart. Here's ours.</p>
                  </AnimatedSection>
                  <div className="ab-process__steps">
                    {steps.map((p, pi) => (
                      <AnimatedSection key={p.step} className="ab-process__step" delay={pi * 100}>
                        <div className="ab-process__step-top">
                          <div className="ab-process__step-icon">{p.icon}</div>
                          {pi < steps.length - 1 && <div className="ab-process__connector" aria-hidden />}
                        </div>
                        <div className="ab-process__step-num">{p.step}</div>
                        <h3 className="ab-process__step-title">{p.title}</h3>
                        <p className="ab-process__step-text">{p.text}</p>
                      </AnimatedSection>
                    ))}
                  </div>
                </section>
              );
            })())}
            {/* Inject gallery before cta */}
            {key === 'cta' && previewSection('cta', (() => {
              const adminGallery = sec['cta']?.galleryImages;
              const gallery = adminGallery?.length > 0
                ? adminGallery.map(g => ({ src: g.url, label: g.caption }))
                : GALLERY;
              return (
                <section className="ab-gallery">
                  <div className="ab-gallery__head">
                    <p className="ab-eyebrow ab-eyebrow--center">Life at Naturiq</p>
                    <h2 className="ab-h2 ab-h2--center">From soil to doorstep</h2>
                  </div>
                  <div className="ab-gallery__grid">
                    {gallery.map((g, gi) => (
                      <div key={gi} className={`ab-gallery__item ab-gallery__item--${gi}`}>
                        <img src={g.src} alt={g.label} loading="lazy" />
                        <div className="ab-gallery__overlay"><span>{g.label}</span></div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })())}
          </React.Fragment>
        );
      })}
    </main>
  );
}